import { useParams, Link, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, type ArticleData } from '@/mocks/articles';
import { mainSiteUrl } from '@/mocks/categories';

function ArticleSectionRenderer({ section, resolvedSlug }: { section: ArticleData['sections'][number]; resolvedSlug: string }) {
  switch (section.type) {
    case 'heading':
      return (
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
          {section.title}
        </h2>
      );

    case 'subheading':
      return (
        <h3 className="font-heading text-xl font-semibold text-foreground-900 mt-6 mb-3">
          {section.title}
        </h3>
      );

    case 'text':
      if (Array.isArray(section.content)) {
        return (
          <>
            {section.content.map((p, i) => (
              <p key={i} className="text-foreground-700 leading-relaxed mb-4">{p}</p>
            ))}
          </>
        );
      }
      return <p className="text-foreground-700 leading-relaxed mb-4">{section.content}</p>;

    case 'benefits':
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {section.benefits?.map((benefit) => (
            <div key={benefit.title} className="p-5 rounded-xl bg-background-100/70 border border-background-200/60">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-check-line"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900">{benefit.title}</h3>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed">{benefit.desc}</p>
            </div>
          ))}
        </div>
      );

    case 'checklist':
      return (
        <div className="p-6 rounded-2xl bg-accent-50 border border-accent-200/50 mb-6">
          <h3 className="font-heading text-lg font-semibold text-accent-800 mb-3 flex items-center gap-2">
            <i className="ri-shield-check-line text-accent-500"></i>
            Essential Checklist
          </h3>
          <ul className="space-y-3">
            {section.checklist?.map((tip, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-foreground-700 leading-relaxed">
                <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 text-xs mt-0.5 font-semibold">
                  {i + 1}
                </span>
                {tip}
              </li>
            ))}
          </ul>
        </div>
      );

    case 'steps':
      return (
        <div className="space-y-4">
          {section.items?.map((item) => (
            <div key={item.step} className="flex gap-4 p-5 rounded-xl bg-background-100/70 border border-background-200/60">
              <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-xl bg-primary-500 text-background-50 font-heading text-lg font-bold">
                {item.step}
              </div>
              <div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      );

    case 'comparison':
      return (
        <div className="rounded-2xl border border-background-200/80 overflow-hidden mb-6">
          <div className="grid grid-cols-3 bg-primary-500">
            <div className="px-4 md:px-6 py-3 text-xs font-semibold text-background-50">Aspect</div>
            <div className="px-4 md:px-6 py-3 text-xs font-semibold text-background-50 text-center">Traditional</div>
            <div className="px-4 md:px-6 py-3 text-xs font-semibold text-background-50 text-center">With Spreadsheet</div>
          </div>
          {section.comparison?.map((row, idx) => (
            <div key={idx} className={`grid grid-cols-3 border-b border-background-200/60 ${idx % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}`}>
              <div className="px-4 md:px-6 py-3 text-sm font-medium text-foreground-800">{row.aspect}</div>
              <div className="px-4 md:px-6 py-3 text-sm text-foreground-500 text-center">{row.traditional}</div>
              <div className="px-4 md:px-6 py-3 text-sm text-primary-700 font-medium text-center bg-primary-50/50">{row.spreadsheet}</div>
            </div>
          ))}
        </div>
      );

    case 'faq':
      return (
        <div className="space-y-3">
          {section.faqs?.map((faq, idx) => (
            <details key={idx} className="group rounded-xl border border-background-200/80 bg-background-50 overflow-hidden">
              <summary className="flex items-center justify-between px-5 py-3.5 cursor-pointer select-none">
                <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 group-open:bg-primary-100 group-open:text-primary-600 transition-colors">
                  <i className="ri-add-line group-open:hidden text-xs"></i>
                  <i className="ri-subtract-line hidden group-open:block text-xs"></i>
                </span>
              </summary>
              <div className="px-5 pb-4">
                <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
              </div>
            </details>
          ))}
        </div>
      );

    case 'cta':
      return (
        <div className="p-8 md:p-10 rounded-2xl bg-primary-500 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
            {section.title}
          </h2>
          <p className="text-primary-100 mb-6 max-w-lg mx-auto text-sm md:text-base">
            {section.content}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-7 py-3 bg-background-50 text-primary-700 text-sm font-semibold rounded-full hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer"
            >
              {articles[resolvedSlug]?.ctaText || 'Access Superbuy Spreadsheet'}
              <i className="ri-arrow-right-up-line ml-2"></i>
            </a>
            <Link
              to="/"
              className="px-7 py-3 text-sm font-semibold text-background-50 border border-background-50/40 rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Back to Home
            </Link>
          </div>
        </div>
      );

    default:
      return null;
  }
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const resolvedSlug = slug || 'complete-guide';
  const article = articles[resolvedSlug];

  // Also handle old path: /what-is-superbuy-spreadsheet
  if (!article) {
    return <Navigate to="/" replace />;
  }

  const structuredSections = article.sections;
  const faqSections = structuredSections.filter(s => s.type === 'faq');

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        {/* Article Hero */}
        <section className="pt-28 pb-12 md:pt-36 md:pb-20 px-4 md:px-6 lg:px-10">
          <div className="max-w-4xl mx-auto">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-foreground-800 transition-colors mb-6 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>

            <div className="flex flex-col lg:flex-row items-start gap-10">
              <div className="flex-1">
                <span className="inline-block px-3 py-1 text-xs font-semibold text-primary-700 bg-primary-100/70 rounded-full mb-4 tracking-wide border border-primary-200/50">
                  Superbuy Spreadsheet {article.category}
                </span>
                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-6">
                  {article.headline}{' '}
                  <span className="text-primary-600">{article.highlightedWord}</span>
                </h1>
                <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
                  {article.intro}
                </p>

                <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    Updated July 2026
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    {article.readingTime}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line"></i>
                    By Superbuy Spreadsheet Team
                  </span>
                </div>
              </div>

              <div className="flex-shrink-0 flex flex-row lg:flex-col gap-3 items-end">
                <div className="text-right">
                  <div className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">2026</div>
                  <div className="text-xs text-foreground-500">Updated</div>
                </div>
                <div className="text-right">
                  <div className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
                    {article.readingTime.split(' ')[0]}
                  </div>
                  <div className="text-xs text-foreground-500">Min Read</div>
                </div>
              </div>
            </div>

            <div className="mt-10 rounded-2xl overflow-hidden">
              <img
                src={article.featuredImage}
                alt={article.featuredImageAlt}
                className="w-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Article Body */}
        <article className="pb-16 px-4 md:px-6 lg:px-10">
          <div className="max-w-3xl mx-auto">
            {structuredSections.map((section, idx) => {
              if (section.type === 'faq') {
                return (
                  <section key={idx} className="mb-14" id="faq">
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                      {section.title || 'Frequently Asked Questions'}
                    </h2>
                    <ArticleSectionRenderer section={section} resolvedSlug={resolvedSlug} />
                  </section>
                );
              }

              if (section.type === 'cta') {
                return (
                  <section key={idx} className="mb-14">
                    <ArticleSectionRenderer section={section} resolvedSlug={resolvedSlug} />
                  </section>
                );
              }

              return (
                <section key={idx} className="mb-14">
                  <ArticleSectionRenderer section={section} resolvedSlug={resolvedSlug} />
                </section>
              );
            })}

            {/* Article Footer Nav */}
            <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-background-200/60">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Homepage
              </Link>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-background-50 bg-primary-500 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Buy It Now
                <i className="ri-external-link-line"></i>
              </a>
            </div>

            {/* Related Articles */}
            <div className="mt-12 pt-8 border-t border-background-200/60">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">
                Explore More Superbuy Spreadsheet Guides
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {Object.values(articles)
                  .filter(a => a.slug !== resolvedSlug)
                  .slice(0, 4)
                  .map((a) => (
                    <Link
                      key={a.slug}
                      to={a.url}
                      className="group p-5 rounded-xl bg-background-100/70 border border-background-200/60 hover:border-primary-200/60 transition-all cursor-pointer"
                    >
                      <span className="text-xs font-semibold text-primary-600 bg-primary-100/70 px-2.5 py-1 rounded-full">
                        {a.category}
                      </span>
                      <h4 className="font-heading text-base font-semibold text-foreground-900 mt-2 mb-1 group-hover:text-primary-700 transition-colors">
                        {a.headline}
                      </h4>
                      <p className="text-xs text-foreground-500">{a.readingTime}</p>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />

      {/* Article Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${article.headline} ${article.highlightedWord}`,
            description: article.metaDescription,
            datePublished: '2026-07-12',
            dateModified: '2026-07-12',
            author: {
              '@type': 'Organization',
              name: 'Superbuy Spreadsheet Team',
            },
            publisher: {
              '@type': 'Organization',
              name: 'Superbuy Spreadsheet',
            },
            image: article.featuredImage,
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `${import.meta.env.VITE_SITE_URL}${article.url}`,
            },
          }),
        }}
      />

      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: `${import.meta.env.VITE_SITE_URL}/`,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Guides',
                item: `${import.meta.env.VITE_SITE_URL}/`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: `${article.headline} ${article.highlightedWord}`,
              },
            ],
          }),
        }}
      />

      {/* FAQ Schema */}
      {faqSections.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqSections.flatMap((s) =>
                (s.faqs || []).map((f) => ({
                  '@type': 'Question',
                  name: f.question,
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: f.answer,
                  },
                }))
              ),
            }),
          }}
        />
      )}
    </div>
  );
}